import React, { useEffect, useRef } from "react";
import "./tablet-nav-overlay.scss";

export const TABLET_NAV_RETURN_FOCUS_SELECTOR = ".mobile-nav__hamburger";

export interface TabletNavOverlayProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly children: React.ReactNode;
  /** Selector for the header toggle that should regain focus when the overlay closes. */
  readonly returnFocusSelector?: string;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const TabletNavOverlay: React.FC<TabletNavOverlayProps> = ({
  isOpen,
  onClose,
  children,
  returnFocusSelector = TABLET_NAV_RETURN_FOCUS_SELECTOR,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    if (!panel) {
      return;
    }

    const getFocusableElements = (): HTMLElement[] =>
      Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex !== -1,
      );

    const focusable = getFocusableElements();
    (focusable[0] ?? panel).focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const items = getFocusableElements();
      if (items.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }

      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);

      // Defer so the hamburger remounts after isOpen becomes false.
      requestAnimationFrame(() => {
        const returnTarget = document.querySelector<HTMLElement>(returnFocusSelector);
        if (returnTarget) {
          returnTarget.focus();
          return;
        }
        previouslyFocusedRef.current?.focus?.();
      });
    };
  }, [isOpen, onClose, returnFocusSelector]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="tablet-nav-overlay"
      role="presentation"
      onClick={onClose}
      data-testid="tablet-nav-overlay"
    >
      <div
        id="tablet-nav-overlay-panel"
        ref={panelRef}
        className="tablet-nav-overlay__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        data-testid="tablet-nav-overlay-panel"
      >
        {children}
      </div>
    </div>
  );
};

export default TabletNavOverlay;
